import React from 'react';
import { Box, Button, Typography, Card, CardContent, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';

const StyledCard = styled(Card)<{ isPopular?: boolean; isCustom?: boolean }>(({ isPopular, isCustom }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    borderRadius: isPopular ? '0 0 16px 16px' : '16px',
    border: isPopular ? '2px solid #FF4B6B' : '1px solid #E5E7EB',
    borderTop: isPopular ? 'none' : undefined,
    background: isCustom ? '#F8F9FA' : '#FFFFFF',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    },
}));

const PriceText = styled(Typography)(({ theme }) => ({
    fontSize: '48px',
    fontWeight: 700,
    color: '#1F2937',
    lineHeight: 1,
}));

const FeatureList = styled(Stack)(({ theme }) => ({
    gap: '12px',
    marginTop: '24px',
    marginBottom: '24px',
}));

const FeatureItem = styled(Box)<{ isIncluded?: boolean; isAddOn?: boolean }>(
    ({ isIncluded = true, isAddOn = false }) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        color: isIncluded ? '#374151' : '#9CA3AF',
        fontSize: '15px', // Slightly larger for readability
        lineHeight: '22px',
    }),
);

const FeatureGroupTitle = styled(Typography)({
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
    marginTop: '16px',
    marginBottom: '8px',
    '&:first-of-type': {
        marginTop: 0,
    },
});

interface ActionButtonProps {
    buttonvariant: 'primary' | 'secondary' | 'tertiary';
    ispopular?: boolean;
    isdisabled?: boolean;
}

const ActionButton = styled(Button)<ActionButtonProps>(({ theme, buttonvariant, ispopular, isdisabled }) => {
    const styles = {
        primary: {
            backgroundColor: '#FF4B6B',
            color: 'white',
            '&:hover': {
                backgroundColor: '#E03E5A',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(255, 75, 107, 0.4)',
            },
        },
        secondary: {
            backgroundColor: ispopular ? '#FF4B6B' : '#374151',
            color: 'white',
            '&:hover': {
                backgroundColor: ispopular ? '#E03E5A' : '#1F2937',
                transform: 'translateY(-1px)',
                boxShadow: ispopular ? '0 4px 12px rgba(255, 75, 107, 0.4)' : '0 4px 12px rgba(55, 65, 81, 0.4)',
            },
        },
        tertiary: {
            backgroundColor: 'transparent',
            color: '#374151',
            border: '2px solid #D1D5DB',
            '&:hover': {
                backgroundColor: '#F9FAFB',
                borderColor: '#9CA3AF',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            },
        },
    };

    const baseStyles = {
        ...styles[buttonvariant],
        borderRadius: '12px',
        padding: '16px 24px',
        fontSize: '16px',
        fontWeight: 600,
        textTransform: 'none' as const,
        width: '100%',
        minHeight: '52px',
        transition: 'all 0.2s ease-in-out',
        boxShadow: buttonvariant === 'tertiary' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)',
    };

    if (isdisabled) {
        return {
            ...baseStyles,
            '&:hover': {
                backgroundColor: baseStyles.backgroundColor,
                transform: 'none',
                boxShadow: baseStyles.boxShadow,
            },
            '&:active': {
                transform: 'none',
            },
            cursor: 'not-allowed',
            opacity: 0.6,
        };
    }

    return baseStyles;
});

export interface FeatureGroup {
    title: string;
    features: Array<{
        text: string;
        isIncluded: boolean;
        isAddOn?: boolean;
    }>;
}

export interface PricingPlan {
    id: string;
    title: string;
    description: string;
    price: number | 'Free' | 'Custom';
    period?: string;
    isPopular?: boolean;
    features: FeatureGroup[];
    buttonText: string;
    buttonVariant: 'primary' | 'secondary' | 'tertiary';
}

interface PricingCardProps {
    plan: PricingPlan;
    onSubscribe: (planId: string) => void;
}

interface ExtendedPricingCardProps extends PricingCardProps {
    isCurrentPlan?: boolean;
    isDowngrade?: boolean;
}

const PricingCard: React.FC<ExtendedPricingCardProps> = ({
    plan,
    onSubscribe,
    isCurrentPlan = false,
    isDowngrade = false,
}) => {
    const navigate = useNavigate();

    const handleButtonClick = () => {
        if (isCurrentPlan) {
            navigate('/account/settings#billing');
        } else if (isDowngrade) {
            return;
        } else {
            onSubscribe(plan.id);
        }
    };

    const renderPrice = () => {
        if (plan.price === 'Free') {
            return (
                <Box display="flex" alignItems="baseline" gap={1}>
                    <PriceText>무료</PriceText>
                    <Typography color="text.secondary" fontSize="14px">
                        / 업로드 5회
                    </Typography>
                </Box>
            );
        }

        if (plan.price === 'Custom') {
            return (
                <Box display="flex" alignItems="baseline" gap={1}>
                    <PriceText>맞춤형</PriceText>
                    <Typography color="text.secondary" fontSize="14px">
                        / 월
                    </Typography>
                </Box>
            );
        }

        return (
            <Box display="flex" alignItems="baseline" gap={1} sx={{ whiteSpace: 'nowrap' }}>
                <PriceText>{plan.price.toLocaleString()}</PriceText>
                <Typography fontSize="20px" color="text.secondary">
                    원
                </Typography>
                <Typography color="text.secondary" fontSize="14px">
                    {plan.period}
                </Typography>
            </Box>
        );
    };

    const renderFeatureIcon = (feature: FeatureGroup['features'][0]) => {
        if (feature.isAddOn) {
            return (
                <AddIcon
                    sx={{
                        fontSize: 20,
                        color: '#FF4B6B',
                        marginTop: '2px',
                        flexShrink: 0,
                    }}
                />
            );
        }

        return (
            <CheckIcon
                sx={{
                    fontSize: 20,
                    color: feature.isIncluded ? '#10B981' : '#E5E7EB',
                    marginTop: '2px',
                    flexShrink: 0,
                }}
            />
        );
    };

    const renderFeatures = () => {
        return plan.features.map((group, groupIndex) => (
            <React.Fragment key={groupIndex}>
                <FeatureGroupTitle sx={groupIndex > 0 ? { marginTop: '16px' } : { marginTop: 0 }}>
                    {group.title}
                </FeatureGroupTitle>
                {group.features.map((feature, featureIndex) => (
                    <FeatureItem
                        key={`${groupIndex}-${featureIndex}`}
                        isIncluded={feature.isIncluded}
                        isAddOn={feature.isAddOn}
                    >
                        {renderFeatureIcon(feature)}
                        <Typography
                            sx={{
                                fontSize: '15px',
                                color: feature.isIncluded ? '#374151' : '#9CA3AF',
                                lineHeight: '24px',
                            }}
                        >
                            {feature.text}
                        </Typography>
                    </FeatureItem>
                ))}
            </React.Fragment>
        ));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* 배지 영역 - 모든 카드에 동일한 높이 확보 */}
            <Box sx={{ height: '40px', display: 'flex', alignItems: 'flex-end' }}>
                {plan.isPopular && (
                    <Box
                        sx={{
                            width: '100%',
                            backgroundColor: '#FF4B6B',
                            color: 'white',
                            textAlign: 'center',
                            padding: '10px 0',
                            fontSize: '14px',
                            fontWeight: 700,
                            borderTopLeftRadius: '16px',
                            borderTopRightRadius: '16px',
                            border: '2px solid #FF4B6B',
                            borderBottom: 'none',
                        }}
                    >
                        인기 플랜
                    </Box>
                )}
            </Box>
            <StyledCard isPopular={plan.isPopular} isCustom={plan.price === 'Custom'}>
                <CardContent
                sx={{
                    padding: '40px 32px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: '22px',
                            fontWeight: 700,
                            color: '#1F2937',
                            marginBottom: '12px',
                        }}
                    >
                        {plan.title}
                    </Typography>

                    <Typography
                        color="text.secondary"
                        sx={{
                            fontSize: '15px',
                            marginBottom: '24px',
                            lineHeight: '1.5',
                            minHeight: '45px', // Align prices
                        }}
                    >
                        {plan.description}
                    </Typography>

                    {renderPrice()}
                </Box>

                <Box sx={{ flex: 1 }}>
                    <FeatureList>{renderFeatures()}</FeatureList>
                </Box>

                <Box sx={{ marginTop: 'auto', paddingTop: '20px' }}>
                    <ActionButton
                        buttonvariant={plan.buttonVariant}
                        ispopular={plan.isPopular}
                        isdisabled={isDowngrade}
                        onClick={handleButtonClick}
                        disabled={false}
                    >
                        {isCurrentPlan ? '구독중' : plan.buttonText}
                    </ActionButton>
                </Box>
            </CardContent>
            </StyledCard>
        </Box>
    );
};

export default PricingCard;
